import axios from "axios";
import useLogin from "hooks/useLogin";
import { URL_FORMS, URL_FORMS_V2 } from "constants/urlApis";
import { endpointError } from "utils/codeErrorsTimeout";
import { isNullOrUndefined } from "utils/validations";

export default function useRecord() {
  const { jwt } = useLogin();
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };

  /**
   * Creates a new record in the specified form.
   * This function sends a POST request to create a new record using the provided `recordData`.
   * The request is made to the endpoint `${URL_FORMS}/form/forms/${formId}/records/` with necessary headers and error handling.
   * @param {Object} recordData - The data to be included in the new record. This object should adhere to the required structure for the record.
   * @param {string} formId - The ID of the form where the record will be created.
   * @returns {Promise<Object>} The response object from the `axios.post` request.
   */
  const createRecord = async (recordData, formId) => {
    const response = await axios.post(`${URL_FORMS}/form/forms/${formId}/records/`, recordData, {
      headers,
      endpointError: endpointError.record.createRecord,
    });
    return response;
  };

  /**
   * Updates an existing record in the specified form.
   * This function sends a PUT request to update the record identified by `recordId` in the form identified by `formId`.
   * The request includes the updated data provided in `recordForm`.
   * @param {Object} recordForm - The updated data for the record. This object should contain the fields that need to be updated.
   * @param {string} formId - The ID of the form where the record is located.
   * @param {string} recordId - The ID of the record to be updated.
   * @returns {Promise<Object>} The response object from the `axios.put` request.
   */
  const updateRecord = async (recordForm, formId, recordId) => {
    const response = await axios.put(
      `${URL_FORMS}/form/forms/${formId}/record/${recordId}/`,
      recordForm,
      {
        headers,
        endpointError: endpointError.record.updateRecord,
      }
    );
    return response;
  };

  /**
   * Retrieves a list of records for the specified form.
   * This function sends a GET request to fetch records from the form identified by `formId`.
   * The request supports pagination through `page` and `per_page` parameters.
   * @param {string} formId - The ID of the form from which records are to be retrieved.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of records per page.
   * @returns {Promise<Object[]>} A promise that resolves to an array of records. The data is retrieved from the `response.data` of the `axios.get` request.
   */
  const getRecords = async (formId, page, per_page) => {
    const response = await axios.get(
      `${URL_FORMS}/form/forms/${formId}/records/?page=${page}&per_page=${per_page}`,
      {
        headers,
        endpointError: endpointError.record.getRecords,
      }
    );
    return response.data;
  };

  /**
   * Retrieves a specific record from a form by its ID.
   * This function sends a GET request to fetch a record identified by `recordId` from the form
   * specified by `formId`. The request includes an `action` parameter for additional filtering or actions.
   * @param {string} formId - The ID of the form from which the record is to be retrieved.
   * @param {string} recordId - The ID of the record to retrieve.
   * @param {string} actions - Additional actions or parameters for the request.
   * @returns {Promise<Object>} A promise that resolves to the record data. The data is retrieved from the `response.data` of the `axios.get` request.
   */
  const getRecordsById = async (formId, recordId, actions) => {
    const response = await axios.get(
      `${URL_FORMS}/form/forms/${formId}/records/${recordId}/?action=${actions}`,
      {
        headers,
        endpointError: endpointError.record.getRecordsById,
      }
    );
    return response.data;
  };

  /**
   * Retrieves files associated with a specific form, optionally filtered by a search subject.
   * This function sends a GET request to fetch files related to the form identified by `formId`.
   * The request can be filtered by `searchedSubject` if provided, and includes pagination with `page` and `per_page` parameters.
   * @param {string} formId - The ID of the form for which files are to be retrieved.
   * @param {string} actions - Additional actions or parameters for the request.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page for pagination.
   * @param {string} [searchedSubject] - Optional search term to filter the files by subject. If not provided, all files are retrieved.
   * @returns {Promise<Object>} A promise that resolves to the data of the files. The data is retrieved from the `response.data` of the `axios.get` request.
   */
  const getFilesByForm = async (formId, actions, page, per_page, searchedSubject) => {
    let response;
    if (isNullOrUndefined(searchedSubject) === false) {
      response = await axios.get(
        `${URL_FORMS_V2}/file/form/${formId}/?action=${actions}&page=${page}&per_page=${per_page}&subject=${searchedSubject}`,
        {
          headers,
          endpointError: endpointError.record.getFilesByForm,
        }
      );
    } else {
      response = await axios.get(
        `${URL_FORMS_V2}/file/form/${formId}/?action=${actions}&page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.record.getFilesByForm,
        }
      );
    }

    return response.data;
  };

  /**
   * Retrieves files associated with a specific record, optionally filtered by a search subject.
   * This function sends a GET request to fetch files related to the record identified by `recordId`.
   * The request can be filtered by `searchedSubject` if provided, and includes pagination with `page` and `per_page` parameters.
   * @param {string} recordId - The ID of the record for which files are to be retrieved.
   * @param {string} actions - Additional actions or parameters for the request.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page for pagination.
   * @param {string} [searchedSubject] - Optional search term to filter the files by subject. If not provided, all files are retrieved.
   * @returns {Promise<Object>} A promise that resolves to the data of the files. The data is retrieved from the `response.data` of the `axios.get` request.
   */
  const getFilesByTRD = async (page, per_page, recordId, searchedSubject, actions) => {
    let response;
    if (isNullOrUndefined(searchedSubject) === false) {
      response = await axios.get(
        `${URL_FORMS_V2}/file/trd/?page=${page}&per_page=${per_page}&record_uuid=${recordId}&subject=${searchedSubject}&action=${actions}`,
        {
          headers,
          endpointError: endpointError.record.getFilesByTRD,
        }
      );
    } else {
      response = await axios.get(
        `${URL_FORMS_V2}/file/trd/?page=${page}&per_page=${per_page}&record_uuid=${recordId}&action=${actions}`,
        {
          headers,
          endpointError: endpointError.record.getFilesByTRD,
        }
      );
    }

    return response.data;
  };

  /**
   * Retrieves files based on TRD roles.
   * Retrieves files based on TRD roles by making a GET request to the server.
   * If a `searchedSubject` is provided, the request includes additional parameters for filtering.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page.
   * @param {string} recordId - The unique identifier for the record.
   * @param {string} searchedSubject - The subject being searched for.
   * @param {string} actions - The actions related to the record.
   * @param {string} roleAction - The role action for the record.
   * @returns {Promise} A promise that resolves with the data of the retrieved files.
   */
  const getFilesByTRDRoles = async (
    page,
    per_page,
    recordId,
    searchedSubject,
    actions,
    roleAction
  ) => {
    let response;
    if (isNullOrUndefined(searchedSubject) === false) {
      response = await axios.get(
        `${URL_FORMS_V2}/file/trd/role-permissions/?page=${page}&per_page=${per_page}&record_uuid=${recordId}&subject=${searchedSubject}&action=${actions}&role_action=${roleAction}`,
        {
          headers,
          endpointError: endpointError.record.getFilesByTRDRoles,
        }
      );
    } else {
      response = await axios.get(
        `${URL_FORMS_V2}/file/trd/role-permissions/?page=${page}&per_page=${per_page}&record_uuid=${recordId}&action=${actions}&role_action=${roleAction}`,
        {
          headers,
          endpointError: endpointError.record.getFilesByTRDRoles,
        }
      );
    }

    return response.data;
  };

  /**
   * Updates a specific file identified by its ID.
   * This function sends a PATCH request to update the file with the given `fileId`.
   * The `data` parameter contains the updated file information.
   * @param {string} fileId - The ID of the file to be updated.
   * @param {Object} data - The updated file data.
   * @param {Object} data - The updated file information to be sent in the PATCH request.
   * @returns {Promise<Object>} A promise that resolves to the data of the updated file. The data is retrieved from the `response.data` of the `axios.patch` request.
   */
  const updateFileById = async (fileId, data) => {
    const response = await axios.patch(`${URL_FORMS}/file/trd/${fileId}/`, data, {
      headers,
      endpointError: endpointError.record.updateFileById,
    });
    return response.data;
  };

  /**
   * Creates a new file in the specified TRD folder and associates it with a record.
   * This function sends a POST request to create a new file in the specified TRD folder
   * and associates it with the record identified by `recordId`. The `formData` parameter
   * contains the file information to be created, and `actions` specifies additional actions
   * to be taken during the file creation process.
   * @param {string} recordId - The ID of the record to which the file is associated.
   * @param {string} folderId - The ID of the TRD folder where the file will be created.
   * @param {Object} formData - The data for the new file to be created. This should include the file content and any metadata.
   * @param {string} actions - Additional actions to be taken during file creation.
   * @returns {Promise<Object>} A promise that resolves to the response from the file creation request. The response contains information about the newly created file.
   */
  const createFileTRD = async (recordId, folderId, formData, actions) => {
    const response = await axios.post(
      `${URL_FORMS}/file/trd/${folderId}/records/${recordId}/?action=${actions}`,
      formData,
      {
        headers,
        endpointError: endpointError.record.createFileTRD,
      }
    );
    return response;
  };

  /**
   * Creates a new file associated with a specific record.
   * This function sends a POST request to create a new file associated with the record
   * identified by `recordId`. The `formData` parameter contains the file information
   * to be created, and `actions` specifies any additional actions to be taken during
   * the file creation process.
   * @param {string} recordId - The ID of the record to which the file is associated.
   * @param {Object} formData - The data for the new file to be created. This should include the file content and any metadata.
   * @param {string} actions - Additional actions to be taken during file creation.
   * @returns {Promise<Object>} A promise that resolves to the response from the file creation request. The response contains information about the newly created file.
   */
  const createFileByRecord = async (recordId, formData, actions) => {
    const response = await axios.post(
      `${URL_FORMS}/file/form/${recordId}/?action=${actions}`,
      formData,
      {
        headers,
        endpointError: endpointError.record.createFileByRecord,
      }
    );
    return response;
  };

  /**
   * Creates a new TRD file associated with a specific task, parent folder, and record.
   * This function sends a POST request to create a new TRD file associated with the
   * record identified by `recordId`, within the folder identified by `parentId`, and
   * related to the task identified by `taskId`. The `formData` parameter contains the
   * file information to be created.
   * @param {string} taskId - The ID of the task associated with the file creation.
   * @param {string} parentId - The ID of the parent folder where the file will be stored.
   * @param {string} recordId - The ID of the record to which the file is associated.
   * @param {Object} formData - The data for the new TRD file to be created. This should include the file content and any metadata.
   * @returns {Promise<Object>} A promise that resolves to the response from the file creation request. The response contains information about the newly created TRD file.
   */
  const createFileTRDByTask = async (taskId, parentId, recordId, formData) => {
    const response = await axios.post(
      `${URL_FORMS}/file/task/${taskId}/trd/${parentId}/records/${recordId}/`,
      formData,
      {
        headers,
        endpointError: endpointError.record.createFileTRDByTask,
      }
    );
    return response;
  };

  /**
   * Creates a new file associated with a specific task and record.
   * This function sends a POST request to create a new file associated with the
   * record identified by `recordId`, and related to the task identified by `taskId`.
   * The `formData` parameter contains the file information to be created.
   * @param {string} taskId - The ID of the task associated with the file creation.
   * @param {string} recordId - The ID of the record to which the file is associated.
   * @param {Object} formData - The data for the new file to be created. This should include the file content and any metadata.
   * @returns {Promise<Object>} A promise that resolves to the response from the file creation request. The response contains information about the newly created file.
   */
  const createFileByTaskRecord = async (taskId, recordId, formData) => {
    const response = await axios.post(
      `${URL_FORMS}/file/task/${taskId}/form/${recordId}/`,
      formData,
      {
        headers,
        endpointError: endpointError.record.createFileByTaskRecord,
      }
    );
    return response;
  };

  /**
   * Creates a new file associated with a specific process, task, and record.
   * This function sends a POST request to create a new file associated with the
   * record identified by `recordId`, and related to the task identified by `taskId`
   * and the parent identified by `parentId`. The `formData` parameter contains
   * the file information to be created.
   * @param {string} taskId - The ID of the task associated with the file creation.
   * @param {string} parentId - The ID of the parent related to the file creation.
   * @param {string} recordId - The ID of the record to which the file is associated.
   * @param {Object} formData - The data for the new file to be created. This should include the file content and any metadata.
   * @returns {Promise<Object>} A promise that resolves to the response from the file creation request. The response contains information about the newly created file.
   */
  const createFileTRDByProcess = async (taskId, parentId, recordId, formData) => {
    const response = await axios.post(
      `${URL_FORMS}/file/process/${taskId}/trd/${parentId}/records/${recordId}/`,
      formData,
      {
        headers,
        endpointError: endpointError.record.createFileTRDByProcess,
      }
    );
    return response;
  };

  /**
   * Creates a new file associated with a specific process and record.
   * This function sends a POST request to create a new file associated with the
   * record identified by `recordId`, and related to the process identified by `taskId`.
   * The `formData` parameter contains the file information to be created.
   * @param {string} taskId - The ID of the process associated with the file creation.
   * @param {string} recordId - The ID of the record to which the file is associated.
   * @param {Object} formData - The data for the new file to be created. This should include the file content and any metadata.
   * @returns {Promise<Object>} A promise that resolves to the response from the file creation request. The response contains information about the newly created file.
   */
  const createFileByProcessRecord = async (taskId, recordId, formData) => {
    const response = await axios.post(
      `${URL_FORMS}/file/process/${taskId}/form/${recordId}/`,
      formData,
      {
        headers,
        endpointError: endpointError.record.createFileByProcessRecord,
      }
    );
    return response;
  };

  /**
   * Retrieves records from a form based on specified filters and pagination.
   * This function sends a POST request to fetch records from the form identified by `formId`.
   * The records are filtered based on the criteria provided in `data`, with pagination
   * controlled by `page` and `per_page` parameters.
   * @param {string} formId - The ID of the form from which records are retrieved.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of records per page for pagination.
   * @param {Object} data - The filtering criteria for retrieving records. This object includes various filter parameters to narrow down the search.
   * @returns {Promise<Object>} A promise that resolves to the data containing the filtered records and pagination information.
   */
  const getRecordsByFilters = async (formId, page, per_page, data) => {
    const response = await axios.post(
      `${URL_FORMS}/form/forms/${formId}/records/search-filter/?page=${page}&per_page=${per_page}`,
      data,
      {
        headers,
        endpointError: endpointError.record.getRecordsByFilters,
      }
    );
    return response.data;
  };

  /**
   * Retrieves free tasks associated with a specific record, with optional filtering by status or search term.
   * This function sends a GET request to fetch free tasks related to the record identified by `recordUuid`.
   * The response can be filtered by task `status` or a search `subject`, and pagination is controlled
   * using `page` and `per_page` parameters.
   * @param {string} recordUuid - The UUID of the record for which free tasks are retrieved.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of tasks per page for pagination.
   * @param {string} [search] - Optional search term to filter tasks by subject.
   * @param {string} [status] - Optional status to filter tasks by their current status.
   * @returns {Promise<Object>} A promise that resolves to the data containing the free tasks and pagination information.
   */
  const getFreeTaskByRecord = async (recordUuid, page, per_page, search, status) => {
    let response;
    if (isNullOrUndefined(status) === false) {
      response = await axios.get(
        `${URL_FORMS}/task/records/${recordUuid}/free-tasks/?page=${page}&per_page=${per_page}&status=${status}`,
        {
          headers,
          endpointError: endpointError.record.getFreeTaskByRecord,
        }
      );
    } else {
      response = await axios.get(
        `${URL_FORMS}/task/records/${recordUuid}/free-tasks/?page=${page}&per_page=${per_page}&subject=${search}`,
        {
          headers,
          endpointError: endpointError.record.getFreeTaskByRecord,
        }
      );
    }

    return response.data;
  };

  /**
   * Retrieves the list of users notified about a specific free task.
   * This function sends a GET request to fetch the users who have been notified about the free task
   * identified by `free_task_uuid`.
   * @param {string} free_task_uuid - The UUID of the free task for which notified users are retrieved.
   * @returns {Promise<Object>} A promise that resolves to the data containing the list of notified users.
   */
  const getNotifiedUsersById = async (free_task_uuid) => {
    const response = await axios.get(`${URL_FORMS}/task/${free_task_uuid}/notified-users/`, {
      headers,
      endpointError: endpointError.record.getNotifiedUsersById,
    });
    return response.data;
  };

  /**
   * Moves a TRD file to a new location.
   * This function sends a PATCH request to move a TRD file to a new location
   * specified by `fileMoveInfo`.
   * @param {string} fileId - The ID of the TRD file to be moved.
   * @param {Object} fileMoveInfo - Information about the new location for the file.
   * @param {string} fileMoveInfo.newLocation - The new location to move the file to.
   * @returns {Promise<Object>} A promise that resolves to the data containing the result of the move operation.
   */
  const moveTrdFileLocation = async (fileId, fileMoveInfo) => {
    const response = await axios.patch(`${URL_FORMS}/file/trd/${fileId}/move/`, fileMoveInfo, {
      headers,
      endpointError: endpointError.record.moveTrdFileLocation,
    });
    return response.data;
  };

  /**
   * Retrieves sticker information for a given record.
   * @async
   * @param {string} recordUuid - The unique identifier of the record.
   * @returns {Promise<Object>} A promise that resolves to the sticker information of the record.
   * @throws Will throw an error if the request fails.
   */
  const getStickerInfo = async (form_uuid, record_uuid) => {
    const response = await axios.get(
      `${URL_FORMS}/form/forms/${form_uuid}/record/${record_uuid}/sticker-data/`,
      {
        headers,
        endpointError: endpointError.record.getStickerInfo,
      }
    );
    return response.data;
  };

  /**
   * Moves a file to the trash by its ID and provides a reason for the action.
   * @param {string} fileId - The ID of the file to be moved to the trash.
   * @param {Object} reason - The reason for moving the file to the trash.
   * @param {string} reason.description - Description explaining why the file is being trashed.
   * @returns {Promise<Object>} - The response data from the server.
   * @throws {Error} - If the request fails.
   */
  const moveFileToTrash = async (fileId, reason) => {
    const response = await axios.post(`${URL_FORMS}/file/${fileId}/move-to-trash/`, reason, {
      headers,
      endpointError: endpointError.record.moveFileToTrash,
    });
    return response.data;
  };

  /**
   * Moves a file to the trash from task box.
   * @param {string} fileId - The ID of the file to be moved to trash.
   * @param {string} reason - The reason for moving the file to trash.
   * @returns {Promise} A promise that resolves with the data of the response after moving the file to trash.
   */
  const moveFileToTrashTaskBox = async (fileId, reason) => {
    const response = await axios.post(
      `${URL_FORMS}/file/${fileId}/move-to-trash/process-task/`,
      reason,
      {
        headers,
        endpointError: endpointError.record.moveFileToTrash,
      }
    );
    return response.data;
  };

  /**
   * Fetches trash data from the server with optional search and filter parameters.
   * @param {number} page - The page number to retrieve.
   * @param {number} per_page - The number of items per page.
   * @param {string} search - The search term to filter the results.
   * @param {string} [filter] - The date filter to apply (optional).
   * @returns {Promise<Object>} - A promise that resolves to the response data.
   */
  const getTrashData = async (page, per_page, search, filter, timeZone) => {
    let response;
    if (isNullOrUndefined(filter) === false) {
      response = await axios.get(
        `${URL_FORMS_V2}/file/trash/?page=${page}&per_page=${per_page}&subject=${search}&date_filter=${filter}&time_zone=${timeZone}`,
        { headers, endpointError: endpointError.record.getTrashData }
      );
    } else {
      response = await axios.get(
        `${URL_FORMS_V2}/file/trash/?page=${page}&per_page=${per_page}&subject=${search}`,
        { headers, endpointError: endpointError.record.getTrashData }
      );
    }
    return response.data;
  };

  /**
   * Restores files from the trash by their IDs.
   * @param {Array<number>} filesId - An array of file IDs to restore from the trash.
   * @returns {Promise<Object>} The response data from the API.
   * @throws Will throw an error if the API request fails.
   */
  const restoreTrashFiles = async (filesId) => {
    const response = await axios.patch(`${URL_FORMS}/file/trash/actions/?action=1`, filesId, {
      headers,
      endpointError: endpointError.record.restoreTrashFiles,
    });
    return response.data;
  };

  /**
   * Sends a PATCH request to delete files by their IDs.
   * This function makes an asynchronous request to the specified endpoint to delete files identified by the provided IDs.
   * The request is sent with the necessary headers and error handling configuration.
   * @param {string[]} filesId - An array of file IDs to be deleted.
   * @returns {Promise<Object>} A promise that resolves to the response data from the server.
   * @throws {Error} Throws an error if the request fails.
   */
  const deleteFiles = async (filesId) => {
    const response = await axios.patch(`${URL_FORMS}/file/trash/actions/?action=2`, filesId, {
      headers,
      endpointError: endpointError.record.deleteFiles,
    });
    return response.data;
  };

  /**
   * Fetches a file from the server for viewing.
   * @function getViewFile
   * @param {string} token - The authentication token required for the request.
   * @returns {Promise<Object>} A promise that resolves to the file data.
   * @throws {Error} Throws an error if the request fails.
   */
  const getViewFile = async (token) => {
    const response = await axios.post(`${URL_FORMS}/file/get-file/`, token, {
      headers,
      responseType: "arraybuffer",
      endpointError: endpointError.record.getViewFile,
    });

    return {
      response: response,
      data: response.data,
      contentType: response.headers["content-type"],
    };
  };

  /**
   * Fetches form permissions for a file by its UUID.
   * @param {string} uuid - The UUID of the file.
   * @returns {Promise<Object>} - A promise that resolves to the response data.
   */
  const getFormPermissionsFilesByUUID = async (uuid) => {
    const response = await axios.get(`${URL_FORMS}/file/${uuid}/form-permissions/`, {
      headers,
      endpointError: endpointError.record.getFormPermissionsFilesByUUID,
    });
    return response.data;
  };

  /**
   * Uploads a file signature with a rubric to the workflow service.
   * @async
   * @function uploadFileSignWithRubric
   * @param {string} fileUuid - The UUID of the file to upload the signature for.
   * @param {FormData} formData - The form data containing the signature and rubric information.
   * @returns {Promise<Object>} The response from the workflow service.
   */
  const uploadFileSignWithRubric = async (fileUuid, formData) => {
    const response = await axios.post(
      `${URL_FORMS}/file/${fileUuid}/profile-signature/`,
      formData,
      {
        headers,
        endpointError: endpointError.record.uploadFileSignWithRubric,
      }
    );
    return response;
  };

  return {
    createRecord,
    updateRecord,
    getRecords,
    getRecordsById,
    getFilesByForm,
    getFilesByTRD,
    getFilesByTRDRoles,
    createFileTRD,
    createFileByRecord,
    createFileTRDByTask,
    createFileByTaskRecord,
    createFileTRDByProcess,
    createFileByProcessRecord,
    getRecordsByFilters,
    getFreeTaskByRecord,
    getNotifiedUsersById,
    updateFileById,
    moveTrdFileLocation,
    getStickerInfo,
    moveFileToTrash,
    moveFileToTrashTaskBox,
    getTrashData,
    restoreTrashFiles,
    deleteFiles,
    getViewFile,
    getFormPermissionsFilesByUUID,
    uploadFileSignWithRubric,
  };
}
